import React, { useEffect, useState, useMemo, memo } from 'react';
import { Fade } from 'react-reveal';

// Imports => Constants
import { KEYS, THEMES, TYPES, VARIANTS } from '@constants';

// Imports => Utilities
import { AcUUID, AcIsEmail, AcIsEmptyString, AcIsSet, AcIsNull } from '@utils';

// Imports => Atoms
import { AcContainer, AcRow, AcColumn } from '@atoms/ac-grid';
import AcBottomDrawer from '@atoms/ac-bottom-drawer/ac-bottom-drawer.web';
import AcHeading from '@atoms/ac-heading/ac-heading.web';
import AcRichContent from '@atoms/ac-rich-content/ac-rich-content.web';
import AcTextInput from '@atoms/ac-text-input/ac-text-input.web';
import AcButton from '@atoms/ac-button/ac-button.web';

let delay = null;

const AcQuestionEmail = ({
	id,
	title,
	question,
	value = '',
	callback,
	skip,
}) => {
	const [email, setEmail] = useState(value);
	const [errors, setErrors] = useState({
		[KEYS.EMAIL]: value ? null : undefined,
	});
	const [drawerVisible, setDrawerVisible] = useState(false);

	const input_id = AcUUID();
	const button_id = AcUUID();

	const hasErrors = useMemo(() => {
		const result = !AcIsNull(errors[KEYS.EMAIL]);
		return result;
	}, [email, errors]);

	const showDrawer = () => {
		const $input = document.getElementById(input_id);

		if ($input && $input === document.activeElement) return;

		if (AcIsSet(email) && AcIsNull(errors[KEYS.EMAIL])) {
			setDrawerVisible(true);
		}
	};

	const hideDrawer = () => {
		setDrawerVisible(false);
	};

	useEffect(() => {
		if (delay) clearTimeout(delay);
		delay = setTimeout(() => {
			if (email) showDrawer();
			else hideDrawer();
		}, 600);
	}, [email]);

	const handleFormSubmit = event => {
		if (event && event.preventDefault) event.preventDefault();
		if (AcIsSet(email) && !AcIsEmptyString(email) && AcIsEmail(email)) {
			if (callback) callback(id, email);
		}
	};

	const handleInputValidation = (name, value, type) => {
		let result = errors;

		switch (name) {
			case KEYS.EMAIL:
				if (AcIsSet(value) && !AcIsEmptyString(value) && !AcIsEmail(value)) {
					result[name] = 'Dit is geen geldig e-mailadres';
				} else {
					result[name] = null;
				}
				break;
			default:
		}

		setErrors(result);

		return result[name];
	};

	const handleInputChange = (event, name, value, type) => {
		if (event && event.persist) event.persist();

		switch (name) {
			case KEYS.EMAIL:
				setEmail(value);
				break;
			default:
		}
	};

	const getEmailInputOptions = useMemo(() => {
		return {
			label: 'E-mailadres',
			type: TYPES.EMAIL,
			name: KEYS.EMAIL,
			value: email,
			theme: THEMES.WHITE,
			required: true,
			validation: handleInputValidation,
			autoComplete: 'new-password',
			callback: handleInputChange,
			onFocus: hideDrawer,
			onBlur: showDrawer,
		};
	}, [email, hideDrawer, showDrawer]);

	const getNoEmailButtonOptions = useMemo(() => {
		return {
			type: TYPES.BUTTON,
			theme: THEMES.WHITE,
			variant: VARIANTS.OUTLINE,
			callback: skip,
			className: 'h-margin-top-30',
		};
	}, []);

	const getSubmitButtonOptions = useMemo(() => {
		return {
			id: button_id,
			type: TYPES.BUTTON,
			theme: THEMES.MINTY,
			disabled: hasErrors,
			title: 'Bevestig',
			callback: handleFormSubmit,
		};
	}, [email, errors, hasErrors]);

	return (
		<>
			<AcContainer
				className={'h-text--align-center h-margin-top-0 h-padding-top-60'}
			>
				{title && (
					<AcRow>
						<AcColumn>
							<AcHeading rank={3} theme={THEMES.WHITE}>
								{title}
							</AcHeading>
						</AcColumn>
					</AcRow>
				)}

				{question && (
					<AcRow>
						<AcColumn>
							<AcRichContent
								content={`<p class="h-text-body-large">${question}</p>`}
								className={'h-text--color-white'}
							/>
						</AcColumn>
					</AcRow>
				)}

				<AcRow>
					<AcColumn>
						<form onSubmit={handleFormSubmit}>
							<AcTextInput {...getEmailInputOptions} />
						</form>
					</AcColumn>
				</AcRow>

				<AcRow>
					<AcColumn>
						<Fade
							bottom
							distance={'20px'}
							duration={200}
							when={hasErrors || AcIsEmptyString(email)}
						>
							<AcButton {...getNoEmailButtonOptions}>
								<span>Geen e-mailadres</span>
							</AcButton>
						</Fade>
					</AcColumn>
				</AcRow>

				<div style={{ height: '10rem' }} />
			</AcContainer>

			<AcBottomDrawer visible={drawerVisible} theme={THEMES.OMEGA}>
				<AcContainer fluid className={'h-text--align-center'}>
					<AcRow>
						<AcColumn>
							<AcButton {...getSubmitButtonOptions}>
								<span>Bevestig</span>
							</AcButton>
						</AcColumn>
					</AcRow>
				</AcContainer>
			</AcBottomDrawer>
		</>
	);
};

export default memo(AcQuestionEmail);
